import React, { useEffect, useRef, useState } from 'react';
import { Button, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import useLoadTracker from '@dr-pam/common-components/Hooks/useLoadTracker';
import NotificationUtils from '@dr-pam/common-components/Utils/NotificationUtils';
import type { NdcAwardCompletion } from '@dr-pam/common-components/Models/NdcProgression';
import DateTimeUtils, { CALENDAR_DATE_FORMAT, DATE_HUMAN_FORMAT } from '@dr-pam/common-components/Utils/DateTimeUtils';
import { getNdcAwardCompletions, setNdcAwardCompletion } from '../../services/NdcAwardCompletionService';

dayjs.extend(customParseFormat);

type CompletionDates = Record<string, string | null>;

const toDates = (completions: NdcAwardCompletion[]): CompletionDates =>
	Object.fromEntries(completions.map(({ programmeId, completedOn }) => [programmeId, completedOn]));

// Typed dates are Australian; the API takes calendar days. Strict, so 31-02-2024 is refused, not
// rolled over to March.
const parseTyped = (typed: string) => {
	const parsed = dayjs(typed, 'DD-MM-YYYY', true);
	return parsed.isValid() ? parsed.format(CALENDAR_DATE_FORMAT) : null;
};

export default function NdcAwardCompletions({ userId }: { userId: string }) {
	const [completions, setCompletions] = useState<NdcAwardCompletion[]>([]);
	const { addLoader, removeLoader, isLoading } = useLoadTracker();
	const form = useForm<CompletionDates>({ initialValues: {} });

	// The user shown; a load or save that finishes after the route moved on must not land here.
	const shownUserId = useRef(userId);

	const showCompletions = (loaded: NdcAwardCompletion[]) => {
		setCompletions(loaded);
		form.setInitialValues(toDates(loaded));
		form.reset();
	};

	useEffect(() => {
		shownUserId.current = userId;
		showCompletions([]);
		const loader = addLoader();
		const request = getNdcAwardCompletions(userId);
		// Cleared on leaving the page as well as on a user switch, so an aborted load is not reported.
		let active = true;
		request.response
			.then((loaded) => active && showCompletions(loaded))
			.catch((err) => active && NotificationUtils.showError(err as Error, 'Failed to load NDC completions.'))
			.finally(() => removeLoader(loader));
		return () => {
			active = false;
			request.abort();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userId]);

	const handleSubmit = async (values: CompletionDates) => {
		const loader = addLoader();
		// The admin may move to another user mid-save: the saves still finish for this one, but the
		// form and the messages belong to whoever is on screen.
		const stillShown = () => shownUserId.current === userId;
		try {
			// Each save lands in the baseline straight away, so a later failure leaves only that programme dirty.
			for (const { programmeId, completedOn } of completions) {
				if (values[programmeId] !== completedOn) {
					const saved = await setNdcAwardCompletion(userId, programmeId, values[programmeId]).response;
					if (stillShown()) {
						setCompletions(saved);
						form.resetDirty(toDates(saved));
					}
				}
			}
			if (stillShown()) {
				NotificationUtils.showSuccess('Successfully updated completion dates', 'Success');
			}
		} catch (err) {
			NotificationUtils.showError(
				err as Error,
				stillShown()
					? 'Failed to update completion dates'
					: 'Failed to update the previous user’s completion dates',
			);
		} finally {
			removeLoader(loader);
		}
	};

	return (
		<form onSubmit={form.onSubmit(handleSubmit)}>
			<Stack mt="xl" maw={420}>
				<Title order={2}>NDC certificates</Title>
				<Text size="sm" c="dimmed">
					The completion date printed on each certificate. It is recorded automatically on completion; enter a
					date only to print a different one, such as for earlier graduates and Fellows. A programme still in
					progress cannot be dated. Clearing an entered date goes back to the recorded one, or hides the
					certificate when there is none.
				</Text>
				{completions.map(({ programmeId, programmeName, completedAt }) => (
					<DateInput
						key={programmeId}
						{...form.getInputProps(programmeId)}
						label={programmeName}
						description={
							completedAt
								? `(DD-MM-YYYY) Recorded as ${DateTimeUtils.format(completedAt, DATE_HUMAN_FORMAT)} where you are`
								: '(DD-MM-YYYY)'
						}
						valueFormat="DD-MM-YYYY"
						dateParser={parseTyped}
						popoverProps={{ withinPortal: true }}
						disabled={isLoading}
						clearable
					/>
				))}
				<Group justify="flex-end">
					<Button type="submit" disabled={isLoading || !form.isDirty()}>
						Save dates {isLoading && <Loader size="xs" ml="xs" />}
					</Button>
				</Group>
			</Stack>
		</form>
	);
}
